import { Box } from '@wix/design-system';
import React, { useContext, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import EnterCode from '../EnterCode';
import { AppContextProvider } from '../AppLoader';
import { loginTwoFactorAuthenticationPageLoad } from '@wix/bi-logger-hls2/v2';
import {
  TwoFactorAuthMethods,
  TwoFactorAuthPhoneDeliveryMethods,
  TwoFactorAuthPhoneDeliveryMethod,
} from '../../utils/constants';
import { OwnerApp2FA } from '../OwnerApp2FA/OwnerApp2FA';
import { EnterCodeContextProvider } from '../EnterCode/EnterCode';
import { EnterCodeStore } from '../../stores/enterCode';
import { TwoFAMethods } from '../TwoFAMethods/TwoFAMethods';
import { TwoFAErrorToast } from '../TwoFAErrorToast/TwoFAErrorToast';
import { TwoFaLobby } from '../TwoFaLobby';
import {
  GenericEventsBiLoggerProvider,
  PageContextProvider,
} from '@wix/data-tools-generic-events';

const TWO_FA_PAGE_NAME = 'login-react-app.two-factor-auth';

const TwoFactorAuthDialog: React.FC = observer(() => {
  const {
    rootStore,
    rootStore: { twoFactorAuthStore, biLogger },
  } = useContext(AppContextProvider);
  const [enterCodeStore] = useState(new EnterCodeStore(rootStore));

  useEffect(() => {
    biLogger.report(
      loginTwoFactorAuthenticationPageLoad({
        origin: 'login',
        flow_type: twoFactorAuthStore.currentTwoFAMethod,
        reason: twoFactorAuthStore.lfndForce2FA ? 'LFND' : 'login',
      }),
    );
  }, [
    biLogger,
    twoFactorAuthStore.currentTwoFAMethod,
    twoFactorAuthStore.lfndForce2FA,
  ]);

  const { t } = useTranslation();
  const showResendButton = twoFactorAuthStore.showResendButton();
  const resendDeliveryMethod =
    twoFactorAuthStore.isTwoFaLobbyEnabled &&
    twoFactorAuthStore.currentTwoFAMethod === TwoFactorAuthMethods.Phone
      ? (twoFactorAuthStore.phoneDeliveryMethod as
          | TwoFactorAuthPhoneDeliveryMethod
          | undefined)
      : undefined;
  const isVoiceCall =
    resendDeliveryMethod === TwoFactorAuthPhoneDeliveryMethods.VOICE_CALL;

  return (
    <GenericEventsBiLoggerProvider value={biLogger}>
      <PageContextProvider pageContext={{ pageName: TWO_FA_PAGE_NAME }}>
        <EnterCodeContextProvider.Provider
          value={{ store: twoFactorAuthStore, enterCodeStore }}
        >
          {twoFactorAuthStore.isTwoFaLobbyEnabled &&
          twoFactorAuthStore.activeView === 'twoFaLobby' ? (
            <TwoFaLobby />
          ) : twoFactorAuthStore.currentTwoFAMethod ===
            TwoFactorAuthMethods.Push ? (
            <OwnerApp2FA />
          ) : (
            <Box direction="vertical" width="100%" align="center">
              <EnterCode
                store={twoFactorAuthStore}
                dialogTitleKey={twoFactorAuthStore.getTitleKey()}
                descriptionKey={twoFactorAuthStore.getDescriptionKey()}
                showRememberMe
              >
                {showResendButton ? (
                  <EnterCode.ButtonsSectionWithResend
                    phoneDeliveryMethod={resendDeliveryMethod}
                    resendLabel={
                      isVoiceCall ? t('enter_code.call_again') : undefined
                    }
                  >
                    <EnterCode.ButtonsSection.VerifyButton />
                  </EnterCode.ButtonsSectionWithResend>
                ) : (
                  <EnterCode.ButtonsSection>
                    <EnterCode.ButtonsSection.VerifyButton />
                  </EnterCode.ButtonsSection>
                )}
                <EnterCode.LinksSection
                  gap={twoFactorAuthStore.isTwoFaLobbyEnabled ? 2 : 1}
                >
                  {!twoFactorAuthStore.isTwoFaLobbyEnabled &&
                    twoFactorAuthStore.currentTwoFAMethod ===
                      TwoFactorAuthMethods.Phone && <EnterCode.VoiceCallLink />}
                  {twoFactorAuthStore.isTwoFaLobbyEnabled
                    ? twoFactorAuthStore.showTwoFaLobbyEntry && (
                        <EnterCode.VerifyAnotherWay />
                      )
                    : twoFactorAuthStore.showMethodsSwitch && <TwoFAMethods />}
                  <EnterCode.LostAccess
                    onClick={twoFactorAuthStore.onLostAccess}
                  />
                </EnterCode.LinksSection>
                <Box marginTop="auto" width="100%">
                  <TwoFAErrorToast />
                </Box>
              </EnterCode>
            </Box>
          )}
        </EnterCodeContextProvider.Provider>
      </PageContextProvider>
    </GenericEventsBiLoggerProvider>
  );
});

export default TwoFactorAuthDialog;
